import { Header } from '@/components/layout/header';
import { LoadingState } from '@/components/loading-state';
import { ErrorState } from '@/components/error-state';
import { StatusBadge } from '@/components/status-badge';
import { useApi } from '@/hooks/use-api';
import {
  divergenceStatus,
  failedStatus,
  slaAgeStatus,
  SLA_PENDING_WARN_HOURS,
} from '@/lib/ingestion-status';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

interface QueueSummary {
  pendingBacklog: number;
  slaPending: number;
  inProgress: number;
  failedTotal: number;
  oldestSlaPendingHours: number | null;
  oldestPendingHours: number | null;
  created24h: number;
  completed24h: number;
}
interface BacklogRow {
  integration: string;
  scope: string;
  source: string | null;
  pending: number;
  oldestHours: number | null;
}
interface ThroughputDay {
  day: string;
  created: number;
  completed: number;
}
interface SlaLatencyDay {
  day: string;
  completed: number;
  p50Mins: number | null;
  p95Mins: number | null;
}
interface ShardStatusRow {
  status: string;
  count: number;
  maxRows: number;
  maxFileBytes: number;
}
interface RollupQueueRow {
  origin: string;
  queued: number;
  oldestLagMins: number | null;
}

const CHART_TOOLTIP_STYLE = {
  backgroundColor: 'var(--card)',
  border: '1px solid var(--border)',
  borderRadius: '8px',
  fontSize: '12px',
} as const;

export function IngestionPage() {
  const summary = useApi<QueueSummary>('/api/ingestion/summary');
  const backlog = useApi<BacklogRow[]>('/api/ingestion/backlog');
  const throughput = useApi<ThroughputDay[]>('/api/ingestion/throughput');
  const slaLatency = useApi<SlaLatencyDay[]>('/api/ingestion/sla-latency');
  const shards = useApi<ShardStatusRow[]>('/api/ingestion/shards');
  const rollup = useApi<RollupQueueRow[]>('/api/ingestion/rollup');

  const all = [summary, backlog, throughput, slaLatency, shards, rollup];
  const anyLoading = all.some((a) => a.loading);
  const errors = all.map((a) => a.error).filter((e): e is string => e !== null);
  const lastFetched = all
    .map((a) => a.lastFetched)
    .filter((d): d is Date => d !== null)
    .sort((a, b) => b.getTime() - a.getTime())[0];

  const refreshAll = () => {
    fetch('/api/cache/invalidate', { method: 'POST' })
      .catch(() => {})
      .finally(() => all.forEach((a) => a.refresh()));
  };

  return (
    <div className="flex flex-col">
      <Header
        title="Ingestion"
        lastFetched={lastFetched ?? null}
        onRefresh={refreshAll}
        loading={anyLoading}
      />
      <div className="space-y-6 p-6">
        {errors.length > 0 && <ErrorState message={errors.join(' · ')} onRetry={refreshAll} />}
        {anyLoading && !summary.data && <LoadingState rows={4} />}
        {summary.data && <SummaryCards s={summary.data} />}
        <div className="grid gap-6 lg:grid-cols-2">
          {throughput.data && <ThroughputChart days={throughput.data} />}
          {slaLatency.data && <SlaLatencyChart days={slaLatency.data} />}
        </div>
        {backlog.data && <BacklogTable rows={backlog.data} />}
        <div className="grid gap-6 lg:grid-cols-2">
          {shards.data && <ShardsTable rows={shards.data} />}
          {rollup.data && <RollupTable rows={rollup.data} />}
        </div>
      </div>
    </div>
  );
}

// --- Summary cards -----------------------------------------------------------

function SummaryCards({ s }: { s: QueueSummary }) {
  const cards: {
    label: string;
    value: string;
    sub: string;
    status: ReturnType<typeof slaAgeStatus>;
  }[] = [
    {
      label: 'Created vs completed (24h)',
      value: `${s.created24h} / ${s.completed24h}`,
      sub: s.created24h > s.completed24h ? 'queue growing' : 'queue keeping up',
      status: divergenceStatus(s.created24h, s.completed24h),
    },
    {
      label: 'Oldest SLA chunk pending',
      value: s.oldestSlaPendingHours === null ? 'none' : `${s.oldestSlaPendingHours}h`,
      sub: `act if > ${SLA_PENDING_WARN_HOURS}h · ${s.slaPending} SLA pending`,
      status: slaAgeStatus(s.oldestSlaPendingHours),
    },
    {
      label: 'Pending backlog',
      value: String(s.pendingBacklog),
      sub: s.oldestPendingHours === null ? 'queue empty' : `oldest waited ${s.oldestPendingHours}h`,
      status: 'healthy',
    },
    {
      label: 'Failed chunks',
      value: String(s.failedTotal),
      sub: 'poison until re-queued',
      status: failedStatus(s.failedTotal),
    },
  ];
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {cards.map((c) => (
        <Card key={c.label}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">{c.label}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-semibold">{c.value}</span>
              {c.status !== 'healthy' && <StatusBadge status={c.status} />}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">{c.sub}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// --- Charts ------------------------------------------------------------------

function ThroughputChart({ days }: { days: ThroughputDay[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Chunks created vs completed (14d)</CardTitle>
        <p className="text-xs text-muted-foreground">
          act if created &gt; completed 2 days running
        </p>
      </CardHeader>
      <CardContent>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={days}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="day" fontSize={11} tickFormatter={(d: string) => d.slice(5)} />
              <YAxis fontSize={11} width={40} />
              <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
              <Bar dataKey="created" fill="var(--chart-2)" name="created" />
              <Bar dataKey="completed" fill="var(--chart-1)" name="completed" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

function SlaLatencyChart({ days }: { days: SlaLatencyDay[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">SLA chunk latency, mins (14d)</CardTitle>
        <p className="text-xs text-muted-foreground">
          created → completed · act if p95 &gt; 60 min
        </p>
      </CardHeader>
      <CardContent>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={days}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="day" fontSize={11} tickFormatter={(d: string) => d.slice(5)} />
              <YAxis fontSize={11} width={40} />
              <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
              <Line
                type="monotone"
                dataKey="p50Mins"
                stroke="var(--chart-1)"
                name="p50"
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="p95Mins"
                stroke="var(--chart-3)"
                name="p95"
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

// --- Tables ------------------------------------------------------------------

function BacklogTable({ rows }: { rows: BacklogRow[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Pending backlog by integration</CardTitle>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">Queue is empty.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Integration</TableHead>
                <TableHead>Scope</TableHead>
                <TableHead>Source</TableHead>
                <TableHead className="text-right">Pending</TableHead>
                <TableHead className="text-right">Oldest (h)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={`${r.integration}-${r.scope}-${r.source}`}>
                  <TableCell>{r.integration}</TableCell>
                  <TableCell>{r.scope}</TableCell>
                  <TableCell>{r.source ?? '—'}</TableCell>
                  <TableCell className="text-right">{r.pending}</TableCell>
                  <TableCell className="text-right">{r.oldestHours ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function ShardsTable({ rows }: { rows: ShardStatusRow[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Raw-data shards</CardTitle>
        <p className="text-xs text-muted-foreground">
          rotation thresholds 25k rows / 150 MB · act if TOMBSTONED grows
        </p>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Count</TableHead>
              <TableHead className="text-right">Largest (rows)</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.status}>
                <TableCell className="flex items-center gap-2">
                  {r.status}
                  {r.status === 'TOMBSTONED' && r.count > 0 && <StatusBadge status="warning" />}
                </TableCell>
                <TableCell className="text-right">{r.count}</TableCell>
                <TableCell className="text-right">{r.maxRows.toLocaleString()}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function RollupTable({ rows }: { rows: RollupQueueRow[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Rollup recompute queue</CardTitle>
        <p className="text-xs text-muted-foreground">act if oldest lag &gt; 60 min</p>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nothing queued — rollup is caught up.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Origin</TableHead>
                <TableHead className="text-right">Queued</TableHead>
                <TableHead className="text-right">Oldest lag (min)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.origin}>
                  <TableCell>{r.origin}</TableCell>
                  <TableCell className="text-right">{r.queued}</TableCell>
                  <TableCell className="text-right">
                    <span className="inline-flex items-center gap-2">
                      {r.oldestLagMins ?? '—'}
                      {(r.oldestLagMins ?? 0) > 60 && <StatusBadge status="warning" />}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
